import React, { useEffect, useRef, useState } from 'react';
import { useField } from 'react-final-form';
import classNames from 'classnames';

import * as tus from 'tus-js-client';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { createVideoUploadUrl, fetchVideoEnabled, fetchVideoStatus } from '../../util/api';

import { InlineTextButton } from '../Button/Button';
import IconSpinner from '../IconSpinner/IconSpinner';

import css from './FieldVideoUpload.module.css';

// Et minuts smykke-video fylder typisk under 50 MB. Graensen er der for at
// stoppe fejlgreb (en times optagelse fra kameraet) foer uploaden, saa
// saelgeren faar en forklaring frem for en fejl efter ti minutters venten.
const MAX_BYTES = 200 * 1024 * 1024;
const ACCEPT = 'video/mp4,video/quicktime,video/webm';

// Behandlingen tager typisk faa sekunder; vi giver den rigeligt, men ikke
// uendeligt - ellers spoerger fanen videre resten af dagen.
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Sender filen direkte til Bunny med tus - genoptagelig upload, saa en telefon
 * der taber forbindelsen midt i et klip kan samle op hvor den slap i stedet
 * for at starte forfra.
 */
const uploadToBunny = (ticket, file) =>
  new Promise((resolve, reject) => {
    const upload = new tus.Upload(file, {
      endpoint: ticket.endpoint,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      headers: {
        AuthorizationSignature: ticket.signature,
        AuthorizationExpire: String(ticket.expire),
        VideoId: ticket.videoId,
        LibraryId: String(ticket.libraryId),
      },
      metadata: {
        filetype: file.type || 'video/mp4',
        title: file.name || 'video',
      },
      onError: reject,
      onSuccess: () => resolve(true),
    });
    upload.start();
  });

/**
 * Nextmine: en kort video paa annoncen.
 *
 * Et smykke er svaert at bedoemme paa stillbilleder - hvordan lyset loeber i en
 * gammel slibning, hvordan et led falder. Feltet er valgfrit og bevidst
 * enkelt: vaelg en fil, vent, faerdig.
 *
 * Vaerdien i formularen er { uid, iframeUrl, thumbnailUrl, durationSeconds } -
 * alt hvad annoncesiden skal bruge for at vise videoen, uden at kende vores
 * konto hos videovaerten.
 *
 * @param {Object} props
 * @param {string} props.name feltets navn i formularen
 * @param {boolean} props.disabled
 */
const FieldVideoUpload = props => {
  const { className, name, disabled } = props;
  const intl = useIntl();
  const inputRef = useRef(null);
  const cancelled = useRef(false);
  const { input } = useField(name, { subscription: { value: true } });

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [enabled, setEnabled] = useState(null);

  useEffect(() => {
    fetchVideoEnabled().then(ok => {
      if (!cancelled.current) {
        setEnabled(ok);
      }
    });
    return () => {
      cancelled.current = true;
    };
  }, []);

  const video = input.value && input.value.uid ? input.value : null;

  const pollUntilReady = async uid => {
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (cancelled.current) {
        return null;
      }
      const status = await fetchVideoStatus(uid);
      if (status.ready) {
        return status;
      }
      if (status.errorReason) {
        throw new Error(status.errorReason);
      }
      if (Date.now() > deadline) {
        throw new Error('timeout');
      }
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
    }
  };

  const handleFile = async e => {
    const file = e.target.files && e.target.files[0];
    // Samme fil to gange i traek skal ogsaa kunne vaelges.
    e.target.value = '';
    if (!file) {
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('tooLarge');
      return;
    }

    setError(null);
    setBusy(true);
    try {
      const ticket = await createVideoUploadUrl();
      await uploadToBunny(ticket, file);
      const status = await pollUntilReady(ticket.videoId);
      if (!status || cancelled.current) {
        return;
      }
      input.onChange({
        uid: ticket.videoId,
        iframeUrl: status.iframeUrl,
        thumbnailUrl: status.thumbnailUrl,
        durationSeconds: status.durationSeconds,
      });
    } catch (err) {
      setError(err?.status === 503 ? 'notConfigured' : 'failed');
    } finally {
      if (!cancelled.current) {
        setBusy(false);
      }
    }
  };

  const handleRemove = () => {
    setError(null);
    input.onChange(null);
  };

  // Er video ikke slaaet til, findes feltet slet ikke - et felt der fejler naar
  // man bruger det, er vaerre end intet felt. Et allerede uploadet klip vises
  // dog stadig, saa det ikke forsvinder lydloest.
  if (enabled === false && !video) {
    return null;
  }

  return (
    <div className={classNames(css.root, className)}>
      <h3 className={css.label}>
        <FormattedMessage id="FieldVideoUpload.label" />
      </h3>
      <p className={css.hint}>
        <FormattedMessage id="FieldVideoUpload.hint" />
      </p>

      {video ? (
        <div className={css.preview}>
          {video.thumbnailUrl ? (
            <img className={css.thumbnail} src={video.thumbnailUrl} alt="" />
          ) : null}
          <div className={css.previewText}>
            <p className={css.ready}>
              <FormattedMessage id="FieldVideoUpload.ready" />
            </p>
            <InlineTextButton type="button" className={css.remove} onClick={handleRemove}>
              <FormattedMessage id="FieldVideoUpload.remove" />
            </InlineTextButton>
          </div>
        </div>
      ) : busy ? (
        <div className={css.busy}>
          <IconSpinner />
          <span className={css.busyText}>
            <FormattedMessage id="FieldVideoUpload.working" />
          </span>
        </div>
      ) : (
        <>
          <input
            ref={inputRef}
            className={css.fileInput}
            id={`${name}-file`}
            type="file"
            accept={ACCEPT}
            disabled={disabled}
            onChange={handleFile}
          />
          <label className={css.chooseButton} htmlFor={`${name}-file`}>
            {intl.formatMessage({ id: 'FieldVideoUpload.choose' })}
          </label>
        </>
      )}

      {error ? (
        <p className={css.error}>
          <FormattedMessage id={`FieldVideoUpload.error.${error}`} />
        </p>
      ) : null}
    </div>
  );
};

export default FieldVideoUpload;
